import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { createStackNavigator } from 'react-navigation-stack';
import { ProfileScreen } from './ProfileScreen';
import { HeaderTitle, InfoAndNotificationBadge } from '../ui';
import { OrganisationLogo } from '../ui/components';
import { OrganisationIcon } from '../ui/components/Icons';
import {
  GoalsTab, ProfileTab, PreferencesTab, BillingTab, ConnectedApps,
} from '../settings';
import { CommunityPartners } from '../WorkoutPartners';
import { BuildHelpNavigator } from '../help';
import FitnessGoal from '../settings/FitnessGoal';
import SkillLevel from '../settings/SkillLevel';
import Theme from '../theme';
import { BackButton } from './components/BackButton';
import { defaultScale } from '../utils/scaling';
import buildWifiNavigator from '../elite/wifi/WifiNavigator';
import EliteEndSessionConfirm from '../elite/EliteEndSessionConfirm';
import buildSwitchMemberNavigator from '../FamilyMembers/SwitchMemberNavigator';
import FaqScreen from '../help/FaqScreen';
import { UpdateProfilePictureScreen } from './components';

const headerLeftImage = () => (Theme.organisationIconUrl
  ? <OrganisationIcon />
  : <OrganisationLogo />);

const styles = StyleSheet.create({
  headerStyle: {
    height: defaultScale(45),
    elevation: 0,
    borderBottomWidth: 0,
    backgroundColor: Theme.CHARCOAL,
  },
  backIcon: {
    paddingHorizontal: defaultScale(10),
  },
  headerWhite: {
    backgroundColor: Theme.WHITE,
    borderBottomWidth: defaultScale(0.5),
    borderBottomColor: Theme.GREY_LIGHT,
  },
});

const profilePictureRouteConfig = {
  UpdateProfilePicture: {
    screen: UpdateProfilePictureScreen,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} color={Theme.PRIMARY} />,
      headerTitle: () => <HeaderTitle title='' />,
      headerStyle: styles.headerWhite,
    }),
  },
};

const defaultStackConfig = (initialRouteName) => ({
  defaultNavigationOptions: () => ({
    initialRouteName,
    headerMode: 'screen',
    headerTintColor: Theme.GREY,
    headerStyle: { ...styles.headerStyle, ...styles.headerWhite },
  }),
});

const defaultNavigationOptionsWithBack = (title) => ({
  navigationOptions: ({ navigation }) => ({
    headerRight: () => <View />,
    headerLeft: () => <BackButton navigation={navigation} />,
    headerTitle: () => <HeaderTitle title={title} />,
    headerStyle: styles.headerWhite,
  }),
});

const buildPersonalSettingsNavigator = () => createStackNavigator(
  {
    PersonalSettings: {
      screen: ProfileTab,
      ...defaultNavigationOptionsWithBack('PERSONAL SETTINGS'),
    },
    ...profilePictureRouteConfig,
  },
  defaultStackConfig('PersonalSettings'),
);

const buildPreferencesNavigator = () => createStackNavigator(
  {
    Preferences: {
      screen: PreferencesTab,
      ...defaultNavigationOptionsWithBack('PREFERENCES'),
    },
    ...profilePictureRouteConfig,
  },
  defaultStackConfig('Preferences'),
);

export const ProfileNavigator = createStackNavigator({
  Profile: {
    screen: ProfileScreen,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: headerLeftImage,
      headerTitle: () => <HeaderTitle title='MORE' />,
      headerStyle: styles.headerWhite,
    }),
  },
  SwitchMember: {
    screen: buildSwitchMemberNavigator(),
    navigationOptions: {
      headerShown: false,
    },
  },
  Goals: {
    screen: GoalsTab,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} />,
      headerTitle: () => <HeaderTitle title='GOALS' />,
      headerStyle: styles.headerWhite,
    }),
  },
  FitnessGoal: {
    screen: FitnessGoal,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} />,
      headerTitle: () => <HeaderTitle title='FITNESS GOAL' />,
      headerStyle: styles.headerWhite,
    }),
  },
  SkillLevel: {
    screen: SkillLevel,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} />,
      headerTitle: () => <HeaderTitle title='SKILL LEVEL' />,
    }),
  },
  PersonalSettings: {
    screen: buildPersonalSettingsNavigator(),
    navigationOptions: {
      headerShown: false,
    },
  },
  Preferences: {
    screen: buildPreferencesNavigator(),
    navigationOptions: {
      headerShown: false,
    },
  },
  WorkoutPartners: {
    screen: CommunityPartners,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} />,
      headerTitle: () => <HeaderTitle title='FRIENDS' />,
      headerStyle: styles.headerWhite,
    }),
  },
  ConnectedApps: {
    screen: ConnectedApps,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} />,
      headerTitle: () => <HeaderTitle title='CONNECTED APPS AND DEVICES' />,
      headerStyle: styles.headerWhite,
    }),
  },
  Billing: {
    screen: BillingTab,
    navigationOptions: ({ navigation }) => ({
      headerRight: () => <View />,
      headerLeft: () => <BackButton navigation={navigation} />,
      headerTitle: () => <HeaderTitle title='BILLING' />,
      headerStyle: styles.headerWhite,
    }),
  },
  Help: {
    screen: BuildHelpNavigator(styles),
    navigationOptions: {
      headerShown: false,
    },
  },
  Wifi: {
    screen: buildWifiNavigator(),
    navigationOptions: {
      headerShown: false,
    },
  },
  confirmSessionEnding: {
    screen: EliteEndSessionConfirm,
    navigationOptions: {
      headerShown: false,
    },
  },
  ...profilePictureRouteConfig,
}, {
  defaultNavigationOptions: ({ navigation }) => ({
    initialRouteName: 'DashboardScreen',
    headerMode: 'screen',
    headerTintColor: Theme.GREY,
    headerStyle: { ...styles.headerStyle, ...styles.headerWhite },
  }),
});
